import { api } from "@/services/api";

export interface VisitorTest {
  id: number;
  visitorId: number;
  appointmentId?: number;
  testTypeId: number;
  testName: string;
  status: 'NOT_STARTED' | 'WAITING_FOR_RESULT' | 'READY_TO_REVIEW' | 'RESULT_UPLOADED' | 'CANCELED';
  resultDocumentUrl?: string;
  resultDocumentFileName?: string;
  resultDocumentContentType?: string;
  otherDocuments?: string[];
  notes?: string;
  uploadedBy?: number;
  scheduledDate?: string;
  completedDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VisitorTestRequest {
  visitorId: number;
  appointmentId?: number;
  testTypeId: number;
  testName: string;
  clientId?: number;
  clientName?: string;
  status: string;
  resultDocumentUrl?: string;
  otherDocuments?: string[];
  notes?: string;
  uploadedBy?: number;
  scheduledDate?: string;
  completedDate?: string;
}

export interface VisitorTestWorkflowStatusRequest {
  visitorTestId?: number;
  visitorId?: number;
  appointmentId?: number;
  testTypeId?: number;
  testName?: string;
  clientId?: number;
  clientName?: string;
  scheduledDate?: string;
  status: "IN_PROGRESS" | "NOT_STARTED" | "WAITING_FOR_RESULT" | "READY_TO_REVIEW" | "RESULT_UPLOADED" | "CANCELED";
}

export const visitorTestApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Super Admin endpoints
    getAllVisitorTests: builder.query<VisitorTest[], void>({
      query: () => "/super-admin/visitor-tests",
      providesTags: ["VisitorTests"],
    }),

    getVisitorTestById: builder.query<VisitorTest, number>({
      query: (id) => `/super-admin/visitor-tests/${id}`,
      providesTags: ["VisitorTests"],
    }),

    createVisitorTest: builder.mutation<VisitorTest, VisitorTestRequest>({
      query: (body) => ({
        url: "/super-admin/visitor-tests",
        method: "POST",
        body,
      }),
      invalidatesTags: ["VisitorTests"],
    }),

    updateVisitorTest: builder.mutation<VisitorTest, { id: number; data: VisitorTestRequest }>({
      query: ({ id, data }) => ({
        url: `/super-admin/visitor-tests/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["VisitorTests"],
    }),

    uploadVisitorTestResultDocument: builder.mutation<VisitorTest, FormData>({
      query: (formData) => ({
        url: "/super-admin/visitor-tests/upload-result",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["VisitorTests", "Orders"],
    }),

    updateVisitorTestWorkflowStatus: builder.mutation<VisitorTest, VisitorTestWorkflowStatusRequest>({
      query: (body) => ({
        url: "/super-admin/visitor-tests/workflow-status",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["VisitorTests", "Orders"],
    }),

    deleteVisitorTest: builder.mutation<void, number>({
      query: (id) => ({
        url: `/super-admin/visitor-tests/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["VisitorTests"],
    }),

    getVisitorTestsByVisitorId: builder.query<VisitorTest[], number>({
      query: (visitorId) => `/super-admin/visitor-tests/visitor/${visitorId}`,
      providesTags: ["VisitorTests"],
    }),

    getVisitorTestsByClientId: builder.query<VisitorTest[], number>({
      query: (clientId) => `/super-admin/visitor-tests/client/${clientId}`,
      providesTags: ["VisitorTests"],
    }),

    getVisitorTestsByStatus: builder.query<VisitorTest[], string>({
      query: (status) => `/super-admin/visitor-tests/status/${status}`,
      providesTags: ["VisitorTests"],
    }),

    getVisitorTestsByVisitorIdAndStatus: builder.query<VisitorTest[], { visitorId: number; status: string }>({
      query: ({ visitorId, status }) => `/super-admin/visitor-tests/visitor/${visitorId}/status/${status}`,
      providesTags: ["VisitorTests"],
    }),

    getVisitorTestsByClientIdAndStatus: builder.query<VisitorTest[], { clientId: number; status: string }>({
      query: ({ clientId, status }) => `/super-admin/visitor-tests/client/${clientId}/status/${status}`,
      providesTags: ["VisitorTests"],
    }),

    getVisitorTestResultDocument: builder.query<Blob, number>({
      query: (id) => ({
        url: `/super-admin/visitor-tests/${id}/result-document`,
        method: "GET",
        responseHandler: async (response: Response) => response.blob(),
      }),
    }),

    deleteVisitorTestResultDocument: builder.mutation<VisitorTest, number>({
      query: (id) => ({
        url: `/super-admin/visitor-tests/${id}/result-document`,
        method: "DELETE",
      }),
      invalidatesTags: ["VisitorTests", "Orders"],
    }),

    // Client endpoints
    getClientVisitorTests: builder.query<VisitorTest[], number>({
      query: (clientId) => `/clients/${clientId}/visitor-tests`,
      providesTags: ["VisitorTests"],
    }),

    getClientVisitorTestsByStatus: builder.query<VisitorTest[], { clientId: number; status: string }>({
      query: ({ clientId, status }) => `/clients/${clientId}/visitor-tests/status/${status}`,
      providesTags: ["VisitorTests"],
    }),
  }),
});

export const {
  useGetAllVisitorTestsQuery,
  useGetVisitorTestByIdQuery,
  useCreateVisitorTestMutation,
  useUpdateVisitorTestMutation,
  useUploadVisitorTestResultDocumentMutation,
  useUpdateVisitorTestWorkflowStatusMutation,
  useDeleteVisitorTestMutation,
  useGetVisitorTestsByVisitorIdQuery,
  useGetVisitorTestsByClientIdQuery,
  useGetVisitorTestsByStatusQuery,
  useGetVisitorTestsByVisitorIdAndStatusQuery,
  useGetVisitorTestsByClientIdAndStatusQuery,
  useLazyGetVisitorTestResultDocumentQuery,
  useDeleteVisitorTestResultDocumentMutation,
  useGetClientVisitorTestsQuery,
  useGetClientVisitorTestsByStatusQuery,
} = visitorTestApi;
